/**
 * ERP Connection Redux slice (SEE-683+).
 *
 * Stores the active ERPConnection record for the selected paidol so
 * downstream consumers (useErpSourceSystem, posting flows, etc.) can
 * cheaply check the paidol's ERP type and connection status without
 * re-querying the CDK AppSync API on every render.
 *
 * Loaded lazily by useErpSourceSystem on selectedPaidolId change.
 *
 * No imports from `app/services/agave/*` or `app/pages/store/agaveSlice` —
 * this slice is the direct-ERP equivalent and stays cleanly separated.
 */

import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getErpConnection, ERPConnectionInfo } from 'app/services/erp-connection.service';
import { signOut } from 'app/store/authSlice';
import { RootState } from 'app/store/rootReducer';

interface ErpConnectionState {
  connectionInfo: ERPConnectionInfo | null;
  loading: boolean;
  error: string | null;
  // Most-recently-requested connectionId. Set in .pending, consulted in
  // .fulfilled / .rejected so a slow A response landing AFTER the user
  // switched to paidol B is discarded instead of overwriting B's state.
  pendingConnectionId: string | null;
}

const initialState: ErpConnectionState = {
  connectionInfo: null,
  loading: false,
  error: null,
  pendingConnectionId: null,
};

/**
 * Fetch the active ERPConnection for a paidol via the CDK `getErpConnection`
 * query. Returns null if the paidol has no `erpConnectionId` set.
 */
export const loadErpConnection = createAsyncThunk<
  ERPConnectionInfo | null,
  { connectionId: string | null | undefined }
>('erpConnection/load', async ({ connectionId }) => {
  if (!connectionId) {
    return null;
  }
  return getErpConnection(connectionId);
});

const erpConnectionSlice = createSlice({
  name: 'erpConnection',
  initialState,
  reducers: {
    /**
     * Reset the slice — used on paidol switch / logout to avoid leaking
     * one paidol's connection info into another's session.
     */
    resetErpConnection: () => initialState,
    /**
     * Local-only update. Used after disconnect/reconnect mutations so the
     * slice reflects the new state without a refetch.
     */
    setErpConnectionInfo: (state, action: PayloadAction<ERPConnectionInfo | null>) => {
      state.connectionInfo = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadErpConnection.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.pendingConnectionId = action.meta.arg.connectionId ?? null;
      })
      .addCase(loadErpConnection.fulfilled, (state, action) => {
        // Guard against the A→B→A-late race: if the user switched paidols
        // while this load was pending, action.meta.arg.connectionId no longer
        // matches state.pendingConnectionId — discard this resolution so the
        // newer request's result is the one that lands.
        const requested = action.meta.arg.connectionId ?? null;
        if (requested !== state.pendingConnectionId) {
          return;
        }
        state.loading = false;
        state.connectionInfo = action.payload;
      })
      .addCase(loadErpConnection.rejected, (state, action) => {
        const requested = action.meta.arg.connectionId ?? null;
        if (requested !== state.pendingConnectionId) {
          return;
        }
        state.loading = false;
        state.error = action.error.message ?? 'Failed to load ERP connection';
        state.connectionInfo = null;
      })
      // Clear connection info on sign-out so one user's ERP connection
      // doesn't leak into the next session on the same device.
      .addCase(signOut.fulfilled, () => initialState);
  },
});

export const { resetErpConnection, setErpConnectionInfo } = erpConnectionSlice.actions;

export const selectErpConnection = (state: RootState): ErpConnectionState => state.erpConnection;
export const selectErpConnectionInfo = (state: RootState): ERPConnectionInfo | null =>
  state.erpConnection.connectionInfo;

export default erpConnectionSlice.reducer;
